import {Component, Inject, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {BackendConfiguration} from '@application/configuration/backend-configuration';
import {MobileNavigationService} from '@application/services/navigation/mobile-navigation.service';
import {NavigationService} from '@application/services/navigation/navigation.service';
import {ApplicationMaintenance} from '@domain/profile/application-maintenance';
import {Permission} from '@domain/profile/permission.enum';
import {ProfileInfo} from '@domain/profile/profile-info';
import {Subscription} from '@domain/profile/subscription';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {CompanySubscriptions} from '@infrastructure/http/profile/company-subscriptions';
import {PROFILE, Profile} from '@infrastructure/http/profile/profile';
import {SignTermsAndConditionsComponent} from '@presentation/components/sign-terms-and-conditons/sign-terms-and-conditions.component';
import {AssertionUtils, BackendError, BaseComponent, DialogBuilderFactoryService, RESPONSIVENESS_VIEW_MODE, ResponsivenessViewMode} from '@vdw/angular-component-library';
import {switchMap, takeUntil} from 'rxjs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent extends BaseComponent implements OnInit {
  public isLoggingIn: boolean;
  private subscriptions: Subscription[];
  public loggedOutAfterSessionExpired = false;
  public showingSubscriptionSelection = false;
  public showingTermsAndConditionsAcceptance = false;

  private applicationMaintenance: ApplicationMaintenance;
  private profileInfo: ProfileInfo;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(PROFILE) private readonly profile: Profile,
    private readonly backendConfiguration: BackendConfiguration,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationService: NavigationService,
    private readonly mobileNavigationService: MobileNavigationService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    @Inject(RESPONSIVENESS_VIEW_MODE) public readonly responsivenessViewMode: ResponsivenessViewMode
  ) {
    super();
  }

  public ngOnInit(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((params: Params) => {
      this.loggedOutAfterSessionExpired = !AssertionUtils.isNullOrUndefined(params['session-expired']);
    });
    this.authenticateUser();
  }

  public login(): void {
    this.loggedOutAfterSessionExpired = false;
    this.isLoggingIn = true;
    this.authentication.login();
  }

  public setSubscriptionAndContinue(subscription: Subscription): void {
    if (!AssertionUtils.isNullOrUndefined(subscription)) {
      this.setSubscriptionAndApplicationMaintenance(subscription);
      this.setProfileInfoAndNavigateToMainPage();
    } else {
      this.isLoggingIn = false;
      this.showingTermsAndConditionsAcceptance = false;
      this.showingSubscriptionSelection = false;
      this.subscriptions = [];
    }
  }

  public signTermsAndConditions(): void {
    this.setSubscriptionAndApplicationMaintenance(this.subscriptions.find((subscription: Subscription) => subscription.id === this.profileInfo.activeSubscription.id));

    this.profile
      .signTermsAndConditions()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => {
        this.navigateToMainPage();
      });
  }

  public canShowLoginForm(): boolean {
    return !this.showingSubscriptionSelection && !this.showingTermsAndConditionsAcceptance;
  }

  private authenticateUser(): void {
    this.authentication
      .isAuthenticationInProgress()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((isAuthenticationInProgress: boolean) => {
        this.isLoggingIn = isAuthenticationInProgress;
      });

    this.authentication
      .onAuthentication()
      .pipe(
        switchMap(() => this.profile.getSubscriptions()),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe({
        next: (companySubscriptions: CompanySubscriptions) => {
          this.subscriptions = companySubscriptions.subscriptions;
          this.applicationMaintenance = companySubscriptions.applicationMaintenance;
          let previousActiveSubscriptionId = this.backendConfiguration.getSubscriptionId();
          let activeSubscription = this.subscriptions.find((t: Subscription) => t.id === previousActiveSubscriptionId);
          if (!AssertionUtils.isNullOrUndefined(previousActiveSubscriptionId) && !AssertionUtils.isNullOrUndefined(activeSubscription)) {
            this.setSubscriptionAndContinue(activeSubscription);
          } else {
            let sortedIndex = this.getSortedIndexBasedOnAlphabets(this.subscriptions);
            this.setSubscriptionAndContinue(this.subscriptions[sortedIndex]);
          }
        },
        error: (error: BackendError) => {
          this.isLoggingIn = false;

          this.authentication.resetLocalStorageToDefaults();
          throw error;
        }
      });
  }

  private getSortedIndexBasedOnAlphabets(subscriptions: Subscription[]): number {
    return subscriptions.reduce((index: number, current: Subscription, currentIndex: number) => {
      if (current.name[0].toUpperCase() < subscriptions[index].name[0].toUpperCase()) {
        return currentIndex;
      }
      return index;
    }, 0);
  }

  private setProfileInfoAndNavigateToMainPage(): void {
    this.profile
      .getProfileInfo()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((profileInfo: ProfileInfo) => {
        this.profileInfo = profileInfo;
        const {activeSubscription} = this.profileInfo;

        if (activeSubscription.hasPermission(Permission.CAN_SIGN_TERMSANDCONDITIONS) && !activeSubscription.acceptedTermsAndConditions) {
          this.showingSubscriptionSelection = false;
          this.backendConfiguration.clearLocalStorage();
          this.authentication.clearSubscription();

          this.showTermsAndConditionsAcceptance();
        } else {
          this.navigateToMainPage();
        }
      });
  }

  private navigateToMainPage(): void {
    const redirect: string = this.authentication.getRedirectAfterAuthentication();

    if (!AssertionUtils.isNullOrUndefined(redirect)) {
      this.authentication.setRedirectAfterAuthentication(null);
      this.router.navigateByUrl(redirect);
    } else {
      const subscription = this.profileInfo.activeSubscription;
      this.router.navigateByUrl(this.responsivenessViewMode.isPhone ? this.mobileNavigationService.getDefaultMobileRoute(subscription) : this.navigationService.getDefaultRoute(subscription));
    }
  }

  private setSubscriptionAndApplicationMaintenance(subscription: Subscription): void {
    this.authentication.setCurrentSubscription(subscription);
    this.backendConfiguration.setApplicationMaintenance(this.applicationMaintenance);
  }

  private showTermsAndConditionsAcceptance(): void {
    this.showingTermsAndConditionsAcceptance = true;

    this.dialogBuilderFactoryService
      .getBuilder()
      .withCloseDisabled()
      .withWidth('605px')
      .openDialog(SignTermsAndConditionsComponent)
      .subscribe(() => this.signTermsAndConditions());
  }
}
